/* Native Shopify slideshow: no library, and no commerce or coupon issuance logic. */
(() => {
  'use strict';
  if (customElements.get('chill-slideshow')) return;
  class ChillSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.track = this.querySelector('[data-slides]');
      this.controls = this.querySelector('[data-slider-controls]');
      if (!this.track || !this.slides.length) return;
      this.index = 0;
      this.selectors = [...this.querySelectorAll('[data-slide-selector]')];
      this.play = this.querySelector('[data-slide-play]');
      this.progress = this.querySelector('[data-slide-progress]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.editor = this.dataset.editor === 'true' || !!window.Shopify?.designMode;
      this.interval = Math.max(6000, Math.min(12000, Number(this.dataset.interval) || 8000));
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches || this.editor;
      this.hovering = false;
      this.visible = true;
      this.style.setProperty('--slide-duration', `${this.interval}ms`);
      this.classList.add('is-ready');
      this.controls.hidden = this.slides.length < 2;
      this.querySelector('[data-slide-total]').textContent = String(this.slides.length).padStart(2, '0');
      this.addEventListener('click', (event) => {
        const selected = event.target.closest('[data-slide-selector]');
        if (selected) this.go(Number(selected.dataset.slideSelector), true);
        if (event.target.closest('[data-slide-next]')) this.go(this.index + 1, true);
        if (event.target.closest('[data-slide-previous]')) this.go(this.index - 1, true);
        if (event.target.closest('[data-slide-play]') && !this.motion.matches && !this.editor) {
          const before = event.detail > 0 && this.playBeforePointer !== undefined ? this.playBeforePointer : this.paused;
          this.playBeforePointer = undefined;
          this.paused = !before;
          this.syncTimer();
        }
      }, options);
      this.play.addEventListener('pointerdown', () => { this.playBeforePointer = this.paused; }, options);
      this.addEventListener('keydown', (event) => {
        if (event.target.closest('a,input,textarea,select,[contenteditable]')) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const rtl = document.documentElement.dir === 'rtl';
        let next = this.index;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.slides.length - 1;
        else next += (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1);
        this.go(next, true);
      }, options);
      this.addEventListener('mouseenter', () => { this.hovering = true; this.syncTimer(); }, options);
      this.addEventListener('mouseleave', () => { this.hovering = false; this.syncTimer(); }, options);
      // Keyboard focus stops rotation until the visitor explicitly presses Play.
      this.addEventListener('focusin', () => { this.paused = true; this.syncTimer(); }, options);
      document.addEventListener('visibilitychange', () => this.syncTimer(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.syncTimer();
      }, options);
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || event.isPrimary === false || event.target.closest('a,button,input')) return;
        this.touch = { x: event.clientX, y: event.clientY, id: event.pointerId, time: Date.now() };
      }, options);
      this.track.addEventListener('pointercancel', () => { this.touch = null; }, options);
      this.track.addEventListener('pointerup', (event) => {
        const start = this.touch;
        this.touch = null;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (Math.abs(dx) < 55 || Math.abs(dx) <= Math.abs(dy) * 1.3 || Date.now() - start.time > 900) return;
        const rtl = document.documentElement.dir === 'rtl';
        this.go(this.index + (dx < 0 ? 1 : -1) * (rtl ? -1 : 1), true);
      }, options);
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === String(event.detail?.blockId));
        if (index < 0 || !this.contains(event.target)) return;
        this.paused = true;
        this.go(index, true);
      }, options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.syncTimer();
        }, { threshold: 0.1 });
        this.observer.observe(this);
      }
      this.go(0, false, true);
    }
    go(next, manual = false, initial = false) {
      if (!this.slides?.length) return;
      if (manual) this.paused = true;
      const previous = this.index;
      this.style.setProperty('--slide-enter-x', next >= previous ? '48px' : '-48px');
      this.index = (next % this.slides.length + this.slides.length) % this.slides.length;
      clearTimeout(this.transitionTimer);
      const focusedSlide = document.activeElement?.closest('[data-slide]');
      if (focusedSlide && this.contains(focusedSlide) && focusedSlide !== this.slides[this.index]) {
        this.track.focus({ preventScroll: true });
      }
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.remove('is-leaving');
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('inert', !active);
        // Keep fallback keyboard behavior safe in engines without native inert support.
        slide.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach((node) => {
          if (!active) {
            if (!node.hasAttribute('data-slider-tabindex')) node.dataset.sliderTabindex = node.getAttribute('tabindex') ?? 'none';
            node.setAttribute('tabindex', '-1');
          } else if (node.hasAttribute('data-slider-tabindex')) {
            if (node.dataset.sliderTabindex === 'none') node.removeAttribute('tabindex');
            else node.setAttribute('tabindex', node.dataset.sliderTabindex);
            delete node.dataset.sliderTabindex;
          }
        });
      });
      if (!initial && previous !== this.index && !this.motion.matches) {
        const outgoing = this.slides[previous];
        outgoing.classList.add('is-leaving');
        this.transitionTimer = setTimeout(() => outgoing.classList.remove('is-leaving'), 900);
      }
      this.selectors.forEach((button, index) => {
        if (index === this.index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      const selected = this.selectors[this.index];
      const group = selected?.parentElement;
      if (group) {
        const left = selected.offsetLeft, right = left + selected.offsetWidth;
        if (left < group.scrollLeft) group.scrollLeft = left;
        else if (right > group.scrollLeft + group.clientWidth) group.scrollLeft = right - group.clientWidth;
      }
      const label = this.slides[this.index].dataset.label;
      this.querySelector('[data-slide-number]').textContent = String(this.index + 1).padStart(2, '0');
      this.querySelector('[data-slide-title]').textContent = label;
      if (manual) this.querySelector('[data-slide-status]').textContent = `Slide ${this.index + 1} of ${this.slides.length}: ${label}`;
      this.syncTimer();
    }
    syncTimer() {
      clearTimeout(this.timer);
      this.timer = null;
      this.classList.remove('is-playing');
      const disabled = this.motion.matches || this.editor;
      this.play.disabled = disabled;
      this.play.setAttribute('aria-label', disabled ? 'Automatic rotation disabled' : this.paused ? 'Play slideshow' : 'Pause slideshow');
      this.play.classList.toggle('is-paused', this.paused || disabled);
      const focusedInSlide = this.track.contains(document.activeElement);
      if (this.slides.length < 2 || this.paused || disabled || this.hovering || !this.visible || document.hidden || focusedInSlide) return;
      // One reflow per transition restarts the visual interval; no frame-by-frame JS.
      void this.progress.offsetWidth;
      this.classList.add('is-playing');
      this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.transitionTimer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
      this.touch = null;
    }
  }
  customElements.define('chill-slideshow', ChillSlideshow);
})();
